import React, { useState, useEffect } from 'react';
import { Loader } from 'lucide-react';
import { useWikimediaSearch } from '../../hooks/useWikimediaSearch';

const WikimediaImageGrid = ({ images, onImageSelect }) => {
  const [thumbnails, setThumbnails] = useState({});
  const [loadingThumbs, setLoadingThumbs] = useState({});
  const { getImageDetails } = useWikimediaSearch();

  useEffect(() => {
    let isMounted = true;

    const loadThumbnails = async () => {
      // Limit to first 30 images to avoid resource exhaustion
      const imagesToLoad = images.slice(0, 30);

      for (const image of imagesToLoad) {
        if (!isMounted) break;
        if (thumbnails[image.id]) continue; // Skip if already loaded

        setLoadingThumbs(prev => ({ ...prev, [image.id]: true }));

        try {
          const details = await getImageDetails(image.title, 200);

          // Only show landscape images (width > height)
          const isLandscape = details.width > details.height;

          if (isMounted && isLandscape) {
            setThumbnails(prev => ({
              ...prev,
              [image.id]: details.thumbUrl
            }));
          } else if (isMounted) {
            // Mark portrait images as skipped to prevent loading spinner
            setThumbnails(prev => ({
              ...prev,
              [image.id]: 'skipped'
            }));
          }
        } catch (err) {
          if (isMounted) {
            console.error('Failed to load thumbnail:', err);
          }
        } finally {
          if (isMounted) {
            setLoadingThumbs(prev => ({ ...prev, [image.id]: false }));
          }
        }
      }
    };

    if (images.length > 0) {
      loadThumbnails();
    }

    return () => {
      isMounted = false;
    };
  }, [images]);

  if (images.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="text-sm text-gray-600 mb-2">
        Found {images.length} images {images.length > 30 && '(showing first 30)'} - click to select
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3 max-h-96 overflow-y-auto">
        {images.map((image) => {
          // Hide portrait images (marked as 'skipped')
          if (thumbnails[image.id] === 'skipped') {
            return null;
          }

          return (
            <div
              key={image.id}
              onClick={() => onImageSelect(image.title)}
              className="cursor-pointer border rounded-lg overflow-hidden hover:border-blue-500 hover:shadow-lg transition-all"
            >
              {thumbnails[image.id] && thumbnails[image.id] !== 'skipped' ? (
                <img
                  src={thumbnails[image.id]}
                  alt={image.title}
                  className="w-full aspect-video object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
                  }}
                />
              ) : (
                <div className="w-full aspect-video flex items-center justify-center bg-gray-100">
                  <Loader className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              )}
              <div className="p-2 bg-gray-50 text-xs truncate" title={image.title.replace('File:', '')}>
                {image.title.replace('File:', '')}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WikimediaImageGrid;
